"use client";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { ITheme, IUserData, IUsersBrandAccounts } from "@/types";
import UserImage from "./sections/UserImage";
import DisplayName from "./sections/DisplayName";
import Biography from "./sections/Biography";
import AdminPopover from "../../content/AdminPopover";
import { SettingTabs } from "../../content/SettingTabs";
import React from "react";
import ProfileImageUpload from "../../settings/profile/ProfileImageUpload";
import Pronoun from "./sections/Pronoun";
import Location from "./sections/Location";
import { Separator } from "@/components/ui/separator";
import { ColorPicker } from "../../content/ColorPicker";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Skeleton } from "@/components/ui/skeleton";
import Customization from "./tabs/Customization";
import ProfileBadges from "@/components/profileBadges";
import CardSetting from "../../settings/CardSetting";
import { cn } from "@/lib/utils";

interface Props {
    user: IUserData;
    admin?: boolean;
    theme: ITheme;
    setTheme: React.Dispatch<React.SetStateAction<ITheme>>;
    selectedPreviewDevice?: string;
    impersonate?: boolean;
    ws?: WebSocket | null;
    loggedInUser?: IUsersBrandAccounts | undefined;
    blogFooter?: boolean;
}

export function HeadingDefault({
    user,
    admin,
    theme,
    setTheme,
    selectedPreviewDevice,
    impersonate,
    ws,
    loggedInUser,
    blogFooter,
}: Props) {
    const [isExpanded, setIsExpanded] = React.useState(false);
    React.useEffect(() => {
        window.addEventListener("closeBlock", {
            handleEvent: (event: CustomEvent) => {
                if (event.detail.id != "1") {
                    setIsExpanded(false);
                }
            },
        });
    });
    const handleClose = () => {
        setIsExpanded(false);
        window.dispatchEvent(new CustomEvent("collapseBlock"));
    };
    const [updateUser, setUpdateUser] = React.useState(user);
    React.useEffect(() => {
        const handleBlocksUpdate = (event: CustomEvent<{ data: any }>) => {
            if (event.detail.data) {
                setUpdateUser({
                    ...updateUser,
                    pic: event.detail.data.pic,
                    displayname: event.detail.data.displayname,
                    bio: event.detail.data.bio,
                    settings: {
                        ...updateUser.settings,
                        pronouns: event.detail.data.pronouns,
                        location: event.detail.data.location,
                    },
                    theme: {
                        ...updateUser.theme,
                        layout: event.detail.data.layout,
                    },
                    design: event.detail.data.design,
                });
            }
        };
        //@ts-expect-error
        window.addEventListener("ws_update_header", handleBlocksUpdate);

        return () => {
            //@ts-expect-error
            window.removeEventListener("ws_update_header", handleBlocksUpdate);
        };
    }, []); // Dependency array is empty because the effect does not depend on any props or state
    const tabs = [
        {
            trigger: "Options",
            content: (
                <div className="flex w-full flex-col gap-3 pb-3">
                    <div className="flex w-full flex-col gap-3">
                        <div>
                            <Label>Profile picture</Label>
                            <ProfileImageUpload
                                user={user}
                                admin={admin}
                                impersonate={impersonate}
                            />
                        </div>
                        <DisplayName
                            user={user}
                            admin={admin}
                            updateUser={updateUser}
                            setUpdateUser={setUpdateUser}
                            impersonate={impersonate}
                            ws={ws}
                            loggedInUser={loggedInUser}
                        />
                        <Separator />
                        <Biography
                            user={user}
                            admin={admin}
                            updateUser={updateUser}
                            setUpdateUser={setUpdateUser}
                            impersonate={impersonate}
                            ws={ws}
                            loggedInUser={loggedInUser}
                        />
                        <Separator />
                        <Pronoun
                            user={user}
                            admin={admin}
                            updateUser={updateUser}
                            setUpdateUser={setUpdateUser}
                            impersonate={impersonate}
                            ws={ws}
                            loggedInUser={loggedInUser}
                        />
                        <Separator />
                        <Location
                            user={user}
                            admin={admin}
                            updateUser={updateUser}
                            setUpdateUser={setUpdateUser}
                            impersonate={impersonate}
                            ws={ws}
                            loggedInUser={loggedInUser}
                        />
                    </div>
                </div>
            ),
        },
        {
            trigger: "Customization",
            content: (
                <Customization
                    user={user}
                    updateUser={updateUser}
                    setUpdateUser={setUpdateUser}
                    impersonate={impersonate}
                    ws={ws}
                    loggedInUser={loggedInUser}
                />
            ),
        },
    ];
    const event = new CustomEvent("expandBlock", {
        detail: {
            title: "Heading",
            content: (
                <div className="flex w-full flex-col gap-3 pb-3">
                    <CardSetting title="Profile picture">
                        <ProfileImageUpload
                            user={user}
                            admin={admin}
                            impersonate={impersonate}
                        />
                    </CardSetting>
                    <Separator />
                    <CardSetting title="Information">
                        <div className="flex w-full flex-col gap-3">
                            <DisplayName
                                user={user}
                                admin={admin}
                                updateUser={updateUser}
                                setUpdateUser={setUpdateUser}
                                impersonate={impersonate}
                                ws={ws}
                                loggedInUser={loggedInUser}
                            />
                            <Separator />
                            <Biography
                                user={user}
                                admin={admin}
                                updateUser={updateUser}
                                setUpdateUser={setUpdateUser}
                                impersonate={impersonate}
                                ws={ws}
                                loggedInUser={loggedInUser}
                            />
                            <Separator />
                            <Pronoun
                                user={user}
                                admin={admin}
                                updateUser={updateUser}
                                setUpdateUser={setUpdateUser}
                                impersonate={impersonate}
                                ws={ws}
                                loggedInUser={loggedInUser}
                            />
                            <Separator />
                            <Location
                                user={user}
                                admin={admin}
                                updateUser={updateUser}
                                setUpdateUser={setUpdateUser}
                                impersonate={impersonate}
                                ws={ws}
                                loggedInUser={loggedInUser}
                            />
                        </div>
                    </CardSetting>
                    <Separator />
                    <CardSetting title="Design">
                        <Customization
                            user={user}
                            updateUser={updateUser}
                            setUpdateUser={setUpdateUser}
                            impersonate={impersonate}
                            ws={ws}
                            loggedInUser={loggedInUser}
                        />
                    </CardSetting>
                </div>
            ),
        },
    });
    const handleExpand = () => {
        window.dispatchEvent(
            new CustomEvent("closeBlock", {
                detail: {
                    id: "1",
                },
            })
        );
        setIsExpanded(true);
        window.dispatchEvent(event);
    };
    React.useEffect(() => {
        if (isExpanded) {
            window.dispatchEvent(event);
        }
        if (updateUser.theme != theme) {
            setTheme({ ...theme, layout: updateUser.theme.layout });
        }
    }, [updateUser]);
    const renderContent = () => {
        switch (updateUser.theme.layout) {
            default:
                return (
                    <div className={`flex w-full flex-col items-center gap-1 `}>
                        <AdminPopover
                            admin={admin}
                            onClick={handleExpand}
                            title="Heading"
                            isOpen={isExpanded}
                            onClose={handleClose}
                            className={cn(
                                admin && "click-admin-profile-heading"
                            )}
                        >
                            <div
                                className="flex w-full flex-col items-center gap-0"
                                id="profile-heading"
                                style={{
                                    color: theme.text_col_heading,
                                }}
                            >
                                <UserImage user={updateUser} admin={admin} />
                                <div className="relative flex w-full items-center justify-center">
                                    <DisplayName
                                        user={user}
                                        updateUser={updateUser}
                                        setUpdateUser={setUpdateUser}
                                        className="text-center"
                                    />
                                    <ProfileBadges
                                        user={user}
                                        colour={theme.text_col_heading}
                                    />
                                </div>
                                <Biography
                                    user={user}
                                    updateUser={updateUser}
                                    setUpdateUser={setUpdateUser}
                                />
                                <div className="flex w-full items-center justify-between">
                                    <Pronoun
                                        user={user}
                                        updateUser={updateUser}
                                        setUpdateUser={setUpdateUser}
                                    />
                                    <Location
                                        user={user}
                                        updateUser={updateUser}
                                        setUpdateUser={setUpdateUser}
                                    />
                                </div>
                            </div>
                        </AdminPopover>
                    </div>
                );
            case "sideways":
                return (
                    <div
                        className={`flex w-full flex-col items-center gap-1 ${blogFooter && "!items-start"}`}
                    >
                        <AdminPopover
                            admin={admin}
                            className={cn(
                                "w-full items-start",
                                admin && "click-admin-profile-heading"
                            )}
                            onClick={handleExpand}
                            title="Heading"
                            isOpen={isExpanded}
                            onClose={handleClose}
                        >
                            <div
                                className="flex items-start gap-4"
                                id="profile-heading"
                                style={{
                                    color: theme.text_col_heading,
                                }}
                            >
                                <UserImage
                                    user={updateUser}
                                    admin={admin}
                                    className={`size-16 md:size-28 ${selectedPreviewDevice === "phone" && "size-16 md:size-16"}`}
                                />
                                <div className="flex w-full flex-col items-start gap-1">
                                    <div className="flex w-full flex-wrap items-center gap-3">
                                        <DisplayName
                                            user={user}
                                            updateUser={updateUser}
                                            setUpdateUser={setUpdateUser}
                                        />
                                        <div className="flex items-center">
                                            <ProfileBadges
                                                user={user}
                                                colour={theme.text_col_heading}
                                            />
                                        </div>
                                    </div>
                                    <Biography
                                        user={user}
                                        updateUser={updateUser}
                                        setUpdateUser={setUpdateUser}
                                    />
                                    <Pronoun
                                        user={user}
                                        updateUser={updateUser}
                                        setUpdateUser={setUpdateUser}
                                    />
                                    <Location
                                        user={user}
                                        updateUser={updateUser}
                                        setUpdateUser={setUpdateUser}
                                    />
                                </div>
                            </div>
                        </AdminPopover>
                    </div>
                );
        }
    };

    return renderContent();
}
